import { UcoastEl } from '@/scripts/core/UcoastEl'
import { canResumeOrigin, listingKind, originKey, originLabel, productPath, readOrigin, returnKey, writeOrigin, type ProductOrigin } from './product-origin-state'

export class ProductOriginNavigation extends UcoastEl {
	static htmlSelector = 'product-origin-navigation'
	private origin?: ProductOrigin
	private storage?: Storage
	private root = '/'
	private cleanupRestore?: () => void

	override connectedCallback() {
		this.root = this.dataset.root || '/'
		if (!this.root.endsWith('/')) this.root += '/'
		try { this.storage = window.sessionStorage } catch { /* Private browsing can disable storage. */ }
		const current = new URL(location.href)
		const product = productPath(current, this.root)
		const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
		let pendingProduct: string | null = null
		try {
			pendingProduct = this.storage?.getItem(originKey + 'next') || null
			this.storage?.removeItem(originKey + 'next')
		} catch { /* Direct entry retains the routes-based fallback. */ }
		const revisiting = navigation?.type === 'reload' || navigation?.type === 'back_forward'
		if (product && (revisiting || pendingProduct === product) && canResumeOrigin(current, document.referrer, this.root, navigation?.type)) {
			this.origin = readOrigin(this.storage, originKey + product, current, this.root)
		} else if (product) {
			try { this.storage?.removeItem(originKey + product) } catch { /* Storage is optional. */ }
		}
		document.querySelectorAll<HTMLAnchorElement>('[data-product-origin-back]').forEach((link) => {
			link.href = this.origin?.url || this.dataset.fallback || link.href
			link.setAttribute('aria-label', originLabel(this.origin, {
				collection: this.dataset.collectionLabel || link.getAttribute('aria-label') || '',
				search: this.dataset.searchLabel || link.getAttribute('aria-label') || '',
			}))
		})
		document.addEventListener('click', this.captureLink)
		if (listingKind(current, this.root) && navigation?.type !== 'back_forward') this.restorePosition(current)
	}

	override disconnectedCallback() {
		document.removeEventListener('click', this.captureLink)
		this.cleanupRestore?.()
	}

	private captureLink = (event: MouseEvent) => {
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
		const link = (event.target as Element)?.closest<HTMLAnchorElement>('a[href]')
		if (!link || link.download || (link.target && link.target !== '_self') || link.closest('quick-add-modal, [data-update-url="false"], [data-product-origin-ignore]')) return
		const current = new URL(location.href)
		const target = new URL(link.href, current)
		if (target.origin !== current.origin) return
		if (link.hasAttribute('data-product-origin-back')) {
			if (this.origin) writeOrigin(this.storage, returnKey, this.origin)
			return
		}
		const product = productPath(target, this.root)
		if (!product || product === productPath(current, this.root)) return
		const kind = listingKind(current, this.root)
		const origin = kind ? { url: current.href, scrollY: window.scrollY, time: Date.now(), kind } : this.origin
		if (origin) {
			writeOrigin(this.storage, originKey + product, origin)
			try { this.storage?.setItem(originKey + 'next', product) } catch { /* Storage is optional. */ }
		}
	}

	private restorePosition(current: URL) {
		const pending = readOrigin(this.storage, returnKey, current, this.root)
		try { this.storage?.removeItem(returnKey) } catch { /* No restoration without storage. */ }
		if (!pending || pending.url !== current.href) return
		// A freshly loaded grid may not have its final height until its media is laid out.
		const restore = () => window.scrollTo({ top: pending.scrollY, behavior: 'instant' })
		const observer = new ResizeObserver(restore)
		observer.observe(document.body)
		const stop = () => {
			observer.disconnect()
			window.removeEventListener('load', restore)
			for (const name of ['wheel', 'touchstart', 'keydown']) window.removeEventListener(name, stop)
			clearTimeout(timer)
		}
		const timer = window.setTimeout(stop, 3000)
		this.cleanupRestore = stop
		window.addEventListener('load', restore, { once: true })
		for (const name of ['wheel', 'touchstart', 'keydown']) window.addEventListener(name, stop, { once: true })
		requestAnimationFrame(restore)
	}
}
